/** @file drawable.add: appends a drawable, creating the sheets it names that do not exist yet (CR-010). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { Warning, WarningCode } from '@zilo/pose3d-format/types/warning';
import { checkDrawable } from '@zilo/pose3d-format/validate/shapes/checkDrawable';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { SHEET_PLACEHOLDER_IMAGE } from '../sheet/sheetPlaceholderImage';
import { requireDrawnAzimuths } from '../facet/requireDrawnAzimuths';
import { pointerToken } from '../validate/pointerToken';
import { readInput } from '../validate/readInput';
import { missingSheetsOf } from './missingSheetsOf';
import { pivotWarnings } from './pivotWarnings';

/** CR-010 adds this code; format's WarningCode union gains it with the CR (S, at merge). */
const SHEET_CREATED: WarningCode = 'SHEET_CREATED';

function createdWarning(sheet: string, existing: string[]): Warning {
  return {
    code: SHEET_CREATED, path: `/puppet/sheets/${pointerToken(sheet)}`, params: { sheet, sheets: existing },
    message: `Sheet ${sheet} did not exist; drawable.add created it as a 1x1 placeholder.`,
    hint: `Fill it with sheet.upload { sheet: '${sheet}', image, size, cells } (or slot.fill, then sheet.pack)`
      + `${existing.length === 0 ? '' : `; existing sheets: ${existing.join(', ')}`}.`,
  };
}

/**
 * `drawable.add { drawable }` → `{ drawable: id }`: a sheet name the puppet lacks becomes a `[1, 1]` sheet on
 * `SHEET_PLACEHOLDER_IMAGE` (replaced by the first `sheet.upload` / `sheet.pack`) with a `SHEET_CREATED` warning each;
 * warns `PIVOT_OUTSIDE`; an entry whose table draws a tile only mirrored (a `_FLIP` twin) needs `drawnAzimuths`. Inverse: `drawable.remove`, or the whole puppet (P) when sheets were created.
 */
export const drawableAddCommand: CommandDef<'drawable.add'> = {
  name: 'drawable.add',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => ({ drawable: readInput(input, path, ['drawable']).check('drawable', checkDrawable) as Drawable }),
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const { id } = input.drawable;
    if (p.drawables.some((d) => d.id === id)) throwError(ERROR_CODES.VALIDATION, { drawable: id }, { path: '/input/drawable/id', hint: 'the drawable id exists' });
    const facets = input.drawable.kind === 'impostor' || input.drawable.kind === 'effect' ? (input.drawable.facets ?? []) : [];
    facets.forEach((e, i) => { requireDrawnAzimuths(p, e, `/input/drawable/facets/${i}`); });
    const missing = missingSheetsOf(p, input.drawable);
    if (missing.length > 0 && ctx.images[SHEET_PLACEHOLDER_IMAGE] === undefined) {
      throwError(ERROR_CODES.NOT_FOUND, { image: SHEET_PLACEHOLDER_IMAGE }, { path: '/input/drawable', hint: 'the sheet placeholder image is not stored; name an existing sheet' });
    }
    const inverse = missing.length > 0 ? puppetInverse(draft) : [{ name: 'drawable.remove' as const, input: { drawable: id } }];
    const existing = Object.keys(p.sheets);
    for (const sheet of missing) p.sheets[sheet] = { image: SHEET_PLACEHOLDER_IMAGE, size: [1, 1] };
    p.drawables.push(structuredClone(input.drawable));
    checkPuppet(draft, ctx);
    const warnings = [...missing.map((s) => createdWarning(s, existing)), ...pivotWarnings(input.drawable, p.drawables.length - 1)];
    return { result: { drawable: id }, warnings, inverse };
  },
};
